import { QueryFunction, QueryKey, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { LazyQueryOptions } from '../types/queries'

export const useLazyQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryKey,
  queryFn,
  onSuccess,
  onError,
  ...queryOptions
}: {
  queryFn: QueryFunction<TQueryFnData>
  queryKey: TQueryKey
} & LazyQueryOptions<TQueryFnData, TError, TData>) => {
  const {
    refetch,
    isLoading, // Warning: isLoading state is omitted, only isFetching is supported for lazy queries
    ...result
  } = useQuery({
    queryKey,
    queryFn,
    enabled: false,
    ...queryOptions,
  })

  const fetchQuery = useCallback(async () => {
    try {
      const { data } = await refetch({ throwOnError: true })
      onSuccess?.(data)
      return data
    } catch (error) {
      onError?.(error as TError)
      throw error
    }
  }, [onError, onSuccess, refetch])

  return { fetchQuery, ...result }
}
